// Client-side "preview a mapping against sample data" for the Mapping edit
// screen - see mappingEngine.ts's header comment for why this never calls
// the backend. This layer adds the parts of zagentc's
// mapping_import.c:emit_sample() that sit *around* the engine (metric
// allowlist, non-finite-value rejection, static-label merge, `at` fallback)
// so preview shows what would actually be sent, not just the raw rule
// output.
import { parseSimpleYaml } from './simpleYaml';
import { parseTextStream } from './sampleText';
import { xmlToRecord } from './xmlRecord';
import { applyMappingSpec, applyTransform, resolvePath, type JsonValue, type MappingSpec } from './mappingEngine';

export type PreviewOutputStatus =
  | 'emitted'
  | 'dropped-required'
  | 'no-name'
  | 'no-rules'
  | 'filtered-allowlist'
  | 'skipped-non-finite'
  // A `to: "signalRef"` rule fired (docs/SIGNAL_TEMPLATES_AS_MAPPING_SCHEMA_PLAN.md
  // Phase 2). This browser-only preview has no server/Redis access to
  // resolve the ref into a real template signal's name (see
  // mappingEngine.ts's OutputOutcome.signalRef comment), so it can only
  // show the unresolved ref itself, never a final PreviewSignal - unlike
  // every other status here, this isn't an error/rejection, just something
  // preview can't fully simulate.
  | 'unresolved-signal-ref';

export interface PreviewSignal {
  name: string;
  value: string;
  at: string; // ISO 8601
  atSynthesized: boolean; // true when no `at` rule matched (import would use "now")
  labels: string[]; // "key=value", in send order (sample labels, then staticLabels - not deduped, matching emit_sample)
}

export interface PreviewOutputResult {
  outputIndex: number;
  status: PreviewOutputStatus;
  signal?: PreviewSignal;
  signalRef?: string; // set only when status is 'unresolved-signal-ref'
}

export interface PreviewRecordResult {
  recordIndex: number;
  record: JsonValue;
  outputs: PreviewOutputResult[];
}

// True if `name` matches any allowlist entry (prefix OR exact). An empty/
// missing allowlist matches everything. Mirrors metric_allowed() in
// mapping_import.c.
function nameAllowed(name: string, allowlist: unknown): boolean {
  if (!Array.isArray(allowlist) || allowlist.length === 0) return true;
  return allowlist.some((pat) => typeof pat === 'string' && name.startsWith(pat));
}

// Mirrors value_is_finite() exactly, crudeness included: it rejects on ANY
// 'n'/'N' or "Inf"/"inf" substring, not just NaN/Infinity's own spelling.
// Preview needs to match real ingestion, not a nicer re-implementation.
function valueIsFinite(value: string | undefined): boolean {
  if (!value) return true;
  if (/[nN]/.test(value)) return false;
  if (/inf/i.test(value)) return false;
  return true;
}

function staticLabelPairs(staticLabels: unknown): string[] {
  if (!staticLabels || typeof staticLabels !== 'object' || Array.isArray(staticLabels)) return [];
  const pairs: string[] = [];
  for (const [k, v] of Object.entries(staticLabels as Record<string, unknown>)) {
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') pairs.push(`${k}=${v}`);
  }
  return pairs;
}

export function previewMapping(
  spec: MappingSpec | undefined,
  records: JsonValue[],
  defaultName: string,
): PreviewRecordResult[] {
  // The transform (group by / sort / select...) runs on the whole list first,
  // exactly as the agent does, so each row shown is what the rules actually see.
  return applyTransform(spec, records).map((record, recordIndex) => {
    const outcomes = applyMappingSpec(spec, record, defaultName);
    const outputs: PreviewOutputResult[] = outcomes.map((outcome, outputIndex) => {
      if (outcome.status !== 'emitted') return { outputIndex, status: outcome.status };

      if (outcome.signalRef !== undefined) {
        return { outputIndex, status: 'unresolved-signal-ref', signalRef: outcome.signalRef };
      }

      if (!nameAllowed(outcome.name!, spec?.metricAllowlist)) {
        return { outputIndex, status: 'filtered-allowlist' };
      }
      if (!valueIsFinite(outcome.value)) {
        return { outputIndex, status: 'skipped-non-finite' };
      }

      const labels = [
        ...Object.entries(outcome.labels).map(([k, v]) => `${k}=${v}`),
        ...staticLabelPairs(spec?.staticLabels),
      ];
      const atSynthesized = outcome.atMs === undefined;
      const at = atSynthesized ? new Date().toISOString() : new Date(outcome.atMs!).toISOString();

      return {
        outputIndex,
        status: 'emitted',
        signal: { name: outcome.name!, value: outcome.value ?? '', at, atSynthesized, labels },
      };
    });
    return { recordIndex, record, outputs };
  });
}

// --- sample parsing -------------------------------------------------------

// Mirrors source_json.c: walk spec.recordPath (if set) to find the Record
// array, or treat a single object response as one Record.
export function parseJsonSample(text: string, recordPath: string | undefined): JsonValue[] {
  const root = JSON.parse(text) as JsonValue;
  const target = recordPath ? resolvePath(root, recordPath) : root;
  if (Array.isArray(target)) {
    return target.filter((r): r is JsonValue => !!r && typeof r === 'object' && !Array.isArray(r));
  }
  if (target && typeof target === 'object') return [target];
  return [];
}

// A small RFC4180-ish splitter mirroring csv_parse.c: quoted fields (incl.
// embedded commas/newlines), "" as an escaped quote, CRLF/LF, blank lines
// skipped. Good enough for pasted sample data; not a general CSV library.
function splitCsvRows(text: string, delimiter = ','): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const n = text.length;

  const pushField = () => {
    row.push(field);
    field = '';
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };

  while (i < n) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"' && field === '') {
      inQuotes = true;
      i++;
      continue;
    }
    if (c === delimiter) {
      pushField();
      i++;
      continue;
    }
    if (c === '\r') {
      i++;
      continue;
    }
    if (c === '\n') {
      pushRow();
      i++;
      continue;
    }
    field += c;
    i++;
  }
  if (field !== '' || row.length > 0) pushRow();

  return rows.filter((r) => !(r.length === 1 && r[0] === ''));
}

// header row -> object keys, one Record per data row - the same shape
// csv_parse.c builds so it can hand rows straight to the mapping engine.
export function parseCsvSample(text: string, delimiter = ','): JsonValue[] {
  const rows = splitCsvRows(text.replace(/^\uFEFF/, ''), delimiter);
  if (rows.length === 0) throw new Error('No header row found.');
  // Skip title/notes lines above the table: the header is the first row as
  // wide as the table's commonest width (Go parity: mappingengine headerIndex).
  const widths = new Map<number, number>();
  rows.forEach((r) => widths.set(r.length, (widths.get(r.length) ?? 0) + 1));
  let w = 0;
  let n = 0;
  widths.forEach((c, k) => {
    if (k > 1 && c > n) {
      w = k;
      n = c;
    }
  });
  const hi = Math.max(0, rows.findIndex((r) => r.length === w));
  const [header, ...dataRows] = rows.slice(hi);
  return dataRows.map((row) => {
    const obj: Record<string, JsonValue> = {};
    header.forEach((key, idx) => {
      obj[key] = row[idx] ?? '';
    });
    return obj;
  });
}

// "csv" is read with whichever separator makes the most regular table (comma,
// semicolon, tab or pipe) - the same choice the server makes.
export function sniffCsvDelimiter(text: string): string {
  let best = ',';
  let bestScore = 0;
  for (const d of [',', ';', '\t', '|']) {
    const widths = new Map<number, number>();
    splitCsvRows(text.replace(/^\uFEFF/, '').slice(0, 200000), d)
      .slice(0, 200)
      .forEach((r) => widths.set(r.length, (widths.get(r.length) ?? 0) + 1));
    widths.forEach((c, k) => {
      if (k > 1 && c * (k - 1) > bestScore) {
        best = d;
        bestScore = c * (k - 1);
      }
    });
  }
  return best;
}

// Record selection shared by every structured-text format (XML, YAML): the
// converted document is walked like a JSON one - recordPath such as
// "feed.entry" picks the repeated element; one match is one Record.
function selectRecords(root: JsonValue, recordPath: string | undefined): JsonValue[] {
  const target = recordPath ? resolvePath(root, recordPath) : root;
  if (Array.isArray(target)) return target.filter((r): r is JsonValue => !!r && typeof r === 'object' && !Array.isArray(r));
  if (target && typeof target === 'object') return [target];
  return [];
}

// XML->Record conversion (xmlRecord.ts, parity with the Go webhook receiver
// and the agent's text_formats.c).
export function parseXmlSample(text: string, recordPath: string | undefined): JsonValue[] {
  return selectRecords(xmlToRecord(text), recordPath);
}

export function parseYamlSample(text: string, recordPath: string | undefined): JsonValue[] {
  return selectRecords(parseSimpleYaml(text), recordPath);
}

// Every format a Record source can be read as. `lines` is a log-like text
// stream: one Record per non-blank line (a JSON object, else logfmt, else
// { line }) - what STDIN/STDOUT/STDERR captures and log files become.
export const SAMPLE_FORMATS = ['json', 'csv', 'tsv', 'xml', 'yaml', 'lines'] as const;
export type SampleFormat = (typeof SAMPLE_FORMATS)[number];

export const isSampleFormat = (v: unknown): v is SampleFormat => typeof v === 'string' && (SAMPLE_FORMATS as readonly string[]).includes(v);

const EXT_FORMATS: Record<string, SampleFormat> = { json: 'json', csv: 'csv', tsv: 'tsv', xml: 'xml', yaml: 'yaml', yml: 'yaml', log: 'lines' };

// The format a pasted/uploaded sample is parsed as: the mapping's own
// sourceType when it names one (csv/tsv/xml/yaml/lines/json agent sources),
// else - for a webhook - the spec's `format` (which the receiver also uses
// when the request's Content-Type doesn't say), else guessed (file
// extension, then first character).
export function detectSampleFormat(sourceType: string, text: string, fileName?: string, specFormat?: string): SampleFormat {
  if (isSampleFormat(sourceType)) return sourceType;
  if (isSampleFormat(specFormat)) return specFormat;
  const ext = fileName?.toLowerCase().split('.').pop();
  if (ext && EXT_FORMATS[ext]) return EXT_FORMATS[ext];
  return text.trimStart().startsWith('<') ? 'xml' : 'json';
}

export function parseSample(sourceType: string, text: string, recordPath: string | undefined, format?: SampleFormat): JsonValue[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  const fmt = format ?? detectSampleFormat(sourceType, trimmed);
  switch (fmt) {
    case 'xml':
      return parseXmlSample(trimmed, recordPath);
    case 'yaml':
      return parseYamlSample(trimmed, recordPath);
    case 'csv':
      return parseCsvSample(text, sniffCsvDelimiter(text));
    case 'tsv':
      return parseCsvSample(text, '\t');
    case 'lines':
      return parseTextStream(text, Number.MAX_SAFE_INTEGER);
    default:
      return parseJsonSample(trimmed, recordPath);
  }
}

// Example text for an empty sample box, by what the source will actually receive.
export function samplePlaceholder(sourceType: string, specFormat?: string): string {
  switch (detectSampleFormat(sourceType, '', undefined, specFormat)) {
    case 'csv':
      return 'name,cpu_pct\nweb-1,42.5';
    case 'tsv':
      return 'name\tcpu_pct\nweb-1\t42.5';
    case 'xml':
      return '<feed><entry host="web-1"><cpu>42.5</cpu></entry></feed>';
    case 'yaml':
      return 'entries:\n  - host: web-1\n    cpu: 42.5';
    case 'lines':
      return 'host=web-1 cpu=42.5\n{"host":"web-2","cpu":58.1}';
    default:
      return '{ "cpu_pct": 42.5, "host": "web-1" }';
  }
}

// What one host-metrics run hands the mapping: one record per collected
// sample, shaped like a Prometheus sample - {name, value, labels:{..}} - so
// transform paths such as "labels.mount" and the usual rules apply. Used to
// seed the editor's sample box for a `system` mapping.
// A small illustrative seed for the Source sample / Live preview panes only
// - NOT the source of truth for which metrics exist to map. That list comes
// from real data (LiveMappingCard/MappingVisualEditor read it from the
// backend's unmapped-values audit + the rule's own already-configured map
// keys - see fetchUnmappedValues), since a hardcoded sample can never track
// what a host's collectors, or a webhook's real payloads, actually send.
export const SYSTEM_SAMPLE = JSON.stringify(
  [
    { name: 'cpu_usage_percent', value: 23.4, labels: { cpu: 'total', host: 'web-1' } },
    { name: 'mem_used_percent', value: 61.2, labels: { host: 'web-1' } },
    { name: 'disk_used_percent', value: 71.5, labels: { mount: '/', device: '/dev/sda1', fstype: 'ext4', host: 'web-1' } },
    { name: 'disk_used_percent', value: 88.1, labels: { mount: '/data', device: '/dev/sdb1', fstype: 'ext4', host: 'web-1' } },
    { name: 'disk_used_percent', value: 12.0, labels: { mount: '/var/log', device: '/dev/sdc1', fstype: 'xfs', host: 'web-1' } },
    { name: 'net_rx_bytes_per_sec', value: 18342, labels: { interface: 'eth0', host: 'web-1' } },
    { name: 'load_1m', value: 0.42, labels: { host: 'web-1' } },
  ],
  null,
  2,
);

// previewMapping for a mapping whose outputs read DIFFERENT lists of the
// document (outputs[i].recordPath, optionally the newest `last` records) - what
// the poller does. Outputs without their own list read `records`, as before.
// Output indexes stay the mapping's real ones.
export function previewMappingPerOutput(
  spec: MappingSpec | undefined,
  records: JsonValue[],
  doc: JsonValue | null,
  defaultName: string,
): PreviewRecordResult[] {
  const outs = Array.isArray(spec?.outputs) ? spec!.outputs! : [];
  if (!spec || !doc || !outs.some((o) => o.recordPath)) return previewMapping(spec, records, defaultName);
  const results: PreviewRecordResult[] = [];
  const run = (indexes: number[], recs: JsonValue[]) => {
    const part = previewMapping({ ...spec, outputs: indexes.map((i) => outs[i]) }, recs, defaultName);
    for (const r of part) results.push({ ...r, recordIndex: results.length, outputs: r.outputs.map((o) => ({ ...o, outputIndex: indexes[o.outputIndex] })) });
  };
  const plain = outs.map((_, i) => i).filter((i) => !outs[i].recordPath);
  if (plain.length > 0) run(plain, records);
  outs.forEach((o, i) => {
    if (!o.recordPath) return;
    const list = resolvePath(doc, o.recordPath);
    let recs = Array.isArray(list) ? list.filter((x): x is JsonValue => x !== null && typeof x === 'object' && !Array.isArray(x)) : [];
    if (typeof o.last === 'number' && o.last >= 1) recs = recs.slice(-o.last);
    if (recs.length > 0) run([i], recs);
  });
  return results;
}
